import { useQuery, UseQueryResult } from 'react-query';
import { getPMOutcomeReport, getPMOutcomeDetail } from '@apis/reports';
import { PMOutcomeReport, PMDetailReport, PMDetailView, PMDistrictOption } from './types';

export class PMDistrictRequiredError extends Error {
  districts: PMDistrictOption[];

  constructor(districts: PMDistrictOption[]) {
    super('Select a district to view progress monitoring outcomes');
    this.name = 'PMDistrictRequiredError';
    this.districts = districts;
  }
}

const PM_DISTRICT_STORAGE_KEY = 'pmOutcomeDistrictId';

export const readStoredPMDistrictId = (): number | null => {
  const stored = sessionStorage.getItem(PM_DISTRICT_STORAGE_KEY);
  if (!stored) return null;
  const parsed = Number(stored);
  return Number.isFinite(parsed) ? parsed : null;
};

export const storePMDistrictId = (districtId: number): void => {
  sessionStorage.setItem(PM_DISTRICT_STORAGE_KEY, String(districtId));
};

export const usePMOutcomeReport = (
  districtId: number | null,
  year: string | null,
  period: string | null,
  compare: string | null,
  bands: Array<{ label: string; lower: number }> | null,
  options?: { enabled?: boolean },
): UseQueryResult<PMOutcomeReport, Error> =>
  useQuery<PMOutcomeReport, Error>(
    ['pmOutcomeReport', districtId, year, period, compare, bands],
    async () => {
      const result = await getPMOutcomeReport(period, compare, bands, year, districtId);
      if ('response' in result && result.response) {
        return result.response.data;
      }
      if ('districts' in result && result.districts?.length) {
        throw new PMDistrictRequiredError(result.districts);
      }
      throw new Error('Failed to load progress monitoring outcomes');
    },
    {
      enabled: options?.enabled ?? true,
      staleTime: 1000 * 60 * 10,
      cacheTime: 1000 * 60 * 30,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  );

export const usePMOutcomeDetail = (
  districtId: number | null,
  year: string | null,
  period: string | null,
  subject: string | null,
  view: PMDetailView | null,
): UseQueryResult<PMDetailReport, Error> =>
  useQuery<PMDetailReport, Error>(
    ['pmOutcomeDetail', districtId, year, period, subject, view],
    async () => {
      const result = await getPMOutcomeDetail(period!, subject!, view!, year, districtId);
      if ('response' in result && result.response) {
        return result.response.data;
      }
      throw new Error('Failed to load progress monitoring detail');
    },
    {
      enabled: !!period && !!subject && !!view,
      staleTime: 1000 * 60 * 10,
      cacheTime: 1000 * 60 * 30,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  );
